import React, { useCallback, useMemo, useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  Square,
  Type,
  Server,
  Cable,
  Box,
  DoorOpen,
  Image as ImageIcon,
  Minus,
  Package,
  Wifi,
  Router,
  Camera,
  Zap,
  HardDrive,
  Monitor,
  Printer,
  Laptop,
  Tv,
  Gamepad2,
  Music,
  Circle,
  Radio,
  Plug,
} from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { useProjectStore } from '../../store/projectStore';
import { requestRestoreFloatingEquipmentPanel } from '../../utils/equipmentPanelUi';
import {
  BrowserGroupKey,
  CanvasBox,
  CanvasLabel,
  PlacedEquipment,
} from '../../types';
import {
  isBrowserGroupVisible,
  isEquipmentTypeVisible,
  isVisibleOnCanvas,
} from '../../utils/browserVisibility';
import { getFloorPlanReferenceImages, wallLengthMeters, formatMeters } from '../../utils/floorPlanGeometry';
import { uniqueCablesById } from '../../utils/cableRuns';
import { useLogicStore } from '../../store/logicStore';

const BROWSER_COLLAPSED_KEY = 'netbuilder-design-browser-collapsed';
const BROWSER_EXPANDED_KEY = 'netbuilder-design-browser-expanded';

let canvasHoverRaf = 0;
let canvasHoverTask: (() => void) | null = null;
function deferCanvasHover(task: () => void) {
  canvasHoverTask = task;
  if (canvasHoverRaf) return;
  canvasHoverRaf = requestAnimationFrame(() => {
    canvasHoverRaf = 0;
    const run = canvasHoverTask;
    canvasHoverTask = null;
    run?.();
  });
}

const TYPE_LABELS: Record<string, string> = {
  switch: 'Switches',
  ap: 'Access Points',
  router: 'Routers',
  server: 'Servers',
  camera: 'Cameras',
  'patch-panel': 'Patch Panels',
  'poe-injector': 'PoE Injectors',
  ups: 'UPS',
  'cable-run': 'Cable Runs',
  computer: 'Computers',
  printer: 'Printers',
  laptop: 'Laptops',
  tv: 'TVs',
  'game-console': 'Game Consoles',
  'media-player': 'Media Players',
  'generic-node': 'Generic Nodes',
  psu: 'PSUs',
  'solar-panel': 'Solar',
  battery: 'Batteries',
  inverter: 'Inverters',
  'wall-outlet': 'Wall Outlets',
  'radio-dish': 'Radio Dishes',
  'sector-antenna': 'Sector Antennas',
};

function equipmentTypeIcon(type: string, size = 11): React.ReactNode {
  const props = { size, strokeWidth: 2 };
  switch (type) {
    case 'ap':
      return <Wifi {...props} />;
    case 'switch':
    case 'router':
      return <Router {...props} />;
    case 'server':
      return <Server {...props} />;
    case 'camera':
      return <Camera {...props} />;
    case 'cable-run':
      return <Cable {...props} />;
    case 'patch-panel':
      return <Package {...props} />;
    case 'poe-injector':
      return <Zap {...props} />;
    case 'psu':
      return <Zap {...props} />;
    case 'solar-panel':
    case 'battery':
    case 'inverter':
      return <Zap {...props} />;
    case 'wall-outlet':
      return <Plug {...props} />;
    case 'ups':
      return <HardDrive {...props} />;
    case 'radio-dish':
    case 'sector-antenna':
      return <Radio {...props} />;
    case 'computer':
      return <Monitor {...props} />;
    case 'printer':
      return <Printer {...props} />;
    case 'laptop':
      return <Laptop {...props} />;
    case 'tv':
      return <Tv {...props} />;
    case 'game-console':
      return <Gamepad2 {...props} />;
    case 'media-player':
      return <Music {...props} />;
    case 'generic-node':
      return <Circle {...props} />;
    default:
      return <Package {...props} />;
  }
}

type ExpandedMap = Record<string, boolean>;

function loadExpanded(): ExpandedMap {
  try {
    const raw = localStorage.getItem(BROWSER_EXPANDED_KEY);
    if (raw) return JSON.parse(raw) as ExpandedMap;
  } catch { /* ignore */ }
  return {
    root: true,
    floorPlan: true,
    equipment: true,
    cables: true,
  };
}

function saveExpanded(map: ExpandedMap) {
  try {
    localStorage.setItem(BROWSER_EXPANDED_KEY, JSON.stringify(map));
  } catch { /* ignore */ }
}

const ROW_H = 22;

interface BrowserTheme {
  isDark: boolean;
  text: string;
  muted: string;
  rowBg: string;
  hoverBg: string;
  selectedBg: string;
}

const EyeBtn: React.FC<{
  visible: boolean;
  onToggle: () => void;
  theme: BrowserTheme;
}> = ({ visible, onToggle, theme }) => (
  <button
    type="button"
    title={visible ? 'Hide' : 'Show'}
    className="flex-shrink-0 p-0.5 rounded hover:bg-black/10"
    style={{ color: visible ? theme.muted : (theme.isDark ? '#64748b' : '#94a3b8') }}
    onClick={(e) => {
      e.stopPropagation();
      e.preventDefault();
      onToggle();
    }}
    onDoubleClick={(e) => {
      e.stopPropagation();
      e.preventDefault();
    }}
  >
    {visible ? <Eye size={12} strokeWidth={2} /> : <EyeOff size={12} strokeWidth={2} />}
  </button>
);

const Row: React.FC<{
  theme: BrowserTheme;
  depth: number;
  label: string;
  selected?: boolean;
  dimmed?: boolean;
  bold?: boolean;
  icon?: React.ReactNode;
  hasChildren?: boolean;
  expanded?: boolean;
  onToggleExpand?: () => void;
  eyeVisible: boolean;
  onToggleEye: () => void;
  onClick?: () => void;
  onDoubleClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  trailing?: React.ReactNode;
}> = ({
  theme,
  depth,
  label,
  selected,
  dimmed,
  bold,
  icon,
  hasChildren,
  expanded: isExp,
  onToggleExpand,
  eyeVisible,
  onToggleEye,
  onClick,
  onDoubleClick,
  onMouseEnter,
  onMouseLeave,
  trailing,
}) => (
  <div
    className={`design-browser-row flex items-center gap-0.5 pr-1 select-none ${onClick || onDoubleClick ? 'cursor-pointer' : 'cursor-default'}`}
    data-selected={selected ? 'true' : undefined}
    style={{
      height: ROW_H,
      paddingLeft: 4 + depth * 12,
      backgroundColor: selected ? theme.selectedBg : theme.rowBg,
      ['--browser-row-hover' as string]: theme.hoverBg,
      opacity: dimmed ? 0.55 : 1,
      color: theme.text,
      fontSize: 11,
      fontWeight: bold || selected ? 600 : 400,
    }}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    onClick={onClick}
    onDoubleClick={onDoubleClick}
  >
    <button
      type="button"
      className="flex-shrink-0 w-3.5 h-3.5 flex items-center justify-center"
      style={{ color: theme.muted, visibility: hasChildren ? 'visible' : 'hidden' }}
      onClick={(e) => {
        e.stopPropagation();
        onToggleExpand?.();
      }}
    >
      {isExp ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
    </button>
    <EyeBtn visible={eyeVisible} onToggle={onToggleEye} theme={theme} />
    <span className="flex-shrink-0 w-3.5 h-3.5 flex items-center justify-center" style={{ color: theme.muted }}>
      {icon}
    </span>
    <span className="truncate flex-1 min-w-0 ml-0.5" title={label}>
      {label}
    </span>
    {trailing}
  </div>
);

export interface DesignBrowserProps {
  isDark: boolean;
  boxes: CanvasBox[];
  labels: Array<CanvasLabel & { isEditing?: boolean }>;
  selectedBoxId: string | null;
  selectedLabelId: string | null;
  onSelectBox: (id: string | null) => void;
  onSelectLabel: (id: string | null) => void;
  onEditLabel: (id: string) => void;
  onToggleBoxHidden: (id: string, hidden: boolean) => void;
  onToggleLabelHidden: (id: string, hidden: boolean) => void;
  onRenameRoom?: (roomId: string) => void;
}

const DesignBrowser: React.FC<DesignBrowserProps> = ({
  isDark,
  boxes,
  labels,
  selectedBoxId,
  selectedLabelId,
  onSelectBox,
  onSelectLabel,
  onEditLabel,
  onToggleBoxHidden,
  onToggleLabelHidden,
  onRenameRoom,
}) => {
  const [panelCollapsed, setPanelCollapsed] = useState(() => {
    try {
      return localStorage.getItem(BROWSER_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [expanded, setExpanded] = useState<ExpandedMap>(loadExpanded);

  const {
    getCurrentDesign,
    getFloorPlan,
    setItemHidden,
    setBrowserGroupVisible,
    setBrowserEquipmentTypeVisible,
    setSelectedEquipment,
    setSelectedCable,
    setSelectedRack,
    setSelectedEquipmentForPortView,
    setPortVisualizationOpen,
    setCablePortEditorOpen,
    setRackBuilderOpen,
    setEquipmentConfigOpen,
    setHoveredEquipmentId,
    setHoveredCableId,
    selectFloorPlanWall,
    setSelectedFloorPlanRoomId,
    setSelectedFloorPlanOpeningId,
    setSelectedReferenceImageId,
    setCanvasMode,
    selectedEquipment,
    selectedCable,
    selectedRack,
    selectedFloorPlanWallId,
    selectedFloorPlanRoomId,
    selectedFloorPlanOpeningId,
    selectedReferenceImageId,
    updateFloorPlanReferenceImage,
  } = useProjectStore(
    useShallow((s) => ({
      getCurrentDesign: s.getCurrentDesign,
      getFloorPlan: s.getFloorPlan,
      setItemHidden: s.setItemHidden,
      setBrowserGroupVisible: s.setBrowserGroupVisible,
      setBrowserEquipmentTypeVisible: s.setBrowserEquipmentTypeVisible,
      setSelectedEquipment: s.setSelectedEquipment,
      setSelectedCable: s.setSelectedCable,
      setSelectedRack: s.setSelectedRack,
      setSelectedEquipmentForPortView: s.setSelectedEquipmentForPortView,
      setPortVisualizationOpen: s.setPortVisualizationOpen,
      setCablePortEditorOpen: s.setCablePortEditorOpen,
      setRackBuilderOpen: s.setRackBuilderOpen,
      setEquipmentConfigOpen: s.setEquipmentConfigOpen,
      setHoveredEquipmentId: s.setHoveredEquipmentId,
      setHoveredCableId: s.setHoveredCableId,
      selectFloorPlanWall: s.selectFloorPlanWall,
      setSelectedFloorPlanRoomId: s.setSelectedFloorPlanRoomId,
      setSelectedFloorPlanOpeningId: s.setSelectedFloorPlanOpeningId,
      setSelectedReferenceImageId: s.setSelectedReferenceImageId,
      setCanvasMode: s.setCanvasMode,
      selectedEquipment: s.selectedEquipment,
      selectedCable: s.selectedCable,
      selectedRack: s.selectedRack,
      selectedFloorPlanWallId: s.selectedFloorPlanWallId,
      selectedFloorPlanRoomId: s.selectedFloorPlanRoomId,
      selectedFloorPlanOpeningId: s.selectedFloorPlanOpeningId,
      selectedReferenceImageId: s.selectedReferenceImageId,
      updateFloorPlanReferenceImage: s.updateFloorPlanReferenceImage,
    }))
  );

  const design = getCurrentDesign();
  const floorPlan = getFloorPlan() || design?.floorPlan || null;

  const toggleExpanded = useCallback((key: string) => {
    setExpanded((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      saveExpanded(next);
      return next;
    });
  }, []);

  const togglePanel = () => {
    setPanelCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem(BROWSER_COLLAPSED_KEY, next ? '1' : '0');
      } catch { /* ignore */ }
      return next;
    });
  };

  const clearOtherSelections = useCallback(() => {
    onSelectBox(null);
    onSelectLabel(null);
    selectFloorPlanWall(null);
    setSelectedFloorPlanRoomId(null);
    setSelectedFloorPlanOpeningId(null);
    setSelectedReferenceImageId(null);
  }, [
    onSelectBox,
    onSelectLabel,
    selectFloorPlanWall,
    setSelectedFloorPlanRoomId,
    setSelectedFloorPlanOpeningId,
    setSelectedReferenceImageId,
  ]);

  const muted = isDark ? '#94a3b8' : '#475569';
  const theme: BrowserTheme = {
    isDark,
    text: isDark ? '#e2e8f0' : '#0f172a',
    muted,
    rowBg: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.55)',
    hoverBg: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(203,213,225,0.92)',
    selectedBg: isDark ? 'rgba(255,255,255,0.16)' : 'rgba(226,232,240,0.9)',
  };

  const equipmentByType = useMemo(() => {
    const map = new Map<string, PlacedEquipment[]>();
    (design?.equipment || []).forEach((eq) => {
      const t = eq.equipment.type;
      if (!map.has(t)) map.set(t, []);
      map.get(t)!.push(eq);
    });
    return map;
  }, [design?.equipment]);

  const cables = useMemo(() => uniqueCablesById(design?.cables), [design?.cables]);
  const refs = floorPlan ? getFloorPlanReferenceImages(floorPlan) : [];
  const doors = (floorPlan?.openings || []).filter((o) => o.kind === 'door');

  const groupVisible = (g: BrowserGroupKey) => isBrowserGroupVisible(design, g);

  if (panelCollapsed) {
    return (
      <div
        className="absolute top-14 left-0 z-40 pointer-events-auto"
        data-design-browser="true"
        style={{ writingMode: 'vertical-rl' }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={togglePanel}
          className="px-1.5 py-2 text-[10px] font-semibold tracking-wider uppercase"
          style={{ color: muted, backgroundColor: 'transparent' }}
          title="Open Browser"
        >
          Browser
        </button>
      </div>
    );
  }

  const designName = design?.name || 'Design';

  return (
    <div
      className="absolute top-14 left-2 z-40 flex flex-col pointer-events-auto overflow-hidden"
      style={{
        width: 228,
        maxHeight: 'calc(100% - 72px)',
        backgroundColor: 'transparent',
      }}
      data-design-browser="true"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
    >
      <div
        className="flex items-center justify-between flex-shrink-0 px-2"
        style={{ height: 26, backgroundColor: 'transparent' }}
      >
        <div className="flex items-center gap-1 min-w-0">
          <button type="button" onClick={togglePanel} className="p-0.5" style={{ color: muted }} title="Collapse">
            <ChevronDown size={12} className="-rotate-90" />
          </button>
          <span className="text-[10px] font-semibold uppercase tracking-wider truncate" style={{ color: muted }}>
            Browser
          </span>
        </div>
      </div>

      <div
        className="flex-1 min-h-0 overflow-y-scroll overflow-x-hidden design-browser-scroll py-0.5"
        style={{ direction: 'rtl' }}
      >
        <div className="min-w-0 overflow-x-hidden" style={{ direction: 'ltr' }}>
        <Row theme={theme}
          depth={0}
          label={designName}
          hasChildren
          expanded={!!expanded.root}
          onToggleExpand={() => toggleExpanded('root')}
          eyeVisible
          onToggleEye={() => {}}
          bold
          icon={<Box size={12} />}
        />

        {expanded.root && (
          <>
            {/* Floor Plan */}
            {(floorPlan?.walls?.length ||
              floorPlan?.rooms?.length ||
              doors.length ||
              refs.length) ? (
              <>
                <Row theme={theme}
                  depth={1}
                  label="Floor Plan"
                  hasChildren
                  expanded={!!expanded.floorPlan}
                  onToggleExpand={() => toggleExpanded('floorPlan')}
                  eyeVisible={
                    groupVisible('walls') ||
                    groupVisible('rooms') ||
                    groupVisible('doors') ||
                    groupVisible('referenceImages')
                  }
                  onToggleEye={() => {
                    const anyOn =
                      groupVisible('walls') ||
                      groupVisible('rooms') ||
                      groupVisible('doors') ||
                      groupVisible('referenceImages');
                    setBrowserGroupVisible('walls', !anyOn);
                    setBrowserGroupVisible('rooms', !anyOn);
                    setBrowserGroupVisible('doors', !anyOn);
                    setBrowserGroupVisible('referenceImages', !anyOn);
                  }}
                  icon={<Minus size={12} />}
                />
                {expanded.floorPlan && (
                  <>
                    {(floorPlan?.walls?.length ?? 0) > 0 && (
                      <>
                        <Row theme={theme}
                          depth={2}
                          label={`Walls (${floorPlan!.walls.length})`}
                          hasChildren
                          expanded={!!expanded.walls}
                          onToggleExpand={() => toggleExpanded('walls')}
                          eyeVisible={groupVisible('walls')}
                          onToggleEye={() => setBrowserGroupVisible('walls', !groupVisible('walls'))}
                          dimmed={!groupVisible('walls')}
                          icon={<Minus size={12} />}
                        />
                        {expanded.walls &&
                          floorPlan!.walls.map((w, i) => {
                            const len = formatMeters(
                              wallLengthMeters(w, floorPlan!.pixelsPerMeter || 50),
                              floorPlan!.units
                            );
                            const selected = selectedFloorPlanWallId === w.id;
                            const visible = isVisibleOnCanvas(design, 'wall', w);
                            return (
                              <Row theme={theme}
                                key={w.id}
                                depth={3}
                                label={`Wall ${i + 1} · ${len}`}
                                selected={selected}
                                dimmed={!visible}
                                eyeVisible={visible}
                                onToggleEye={() => setItemHidden('wall', w.id, !w.hidden)}
                                icon={<Minus size={11} />}
                                onClick={() => {
                                  clearOtherSelections();
                                  setSelectedEquipment(null);
                                  setSelectedCable(null);
                                  setSelectedRack(null);
                                  setCanvasMode('select');
                                  selectFloorPlanWall(w.id);
                                }}
                              />
                            );
                          })}
                      </>
                    )}

                    {(floorPlan?.rooms?.length ?? 0) > 0 && (
                      <>
                        <Row theme={theme}
                          depth={2}
                          label={`Rooms (${floorPlan!.rooms.length})`}
                          hasChildren
                          expanded={!!expanded.rooms}
                          onToggleExpand={() => toggleExpanded('rooms')}
                          eyeVisible={groupVisible('rooms')}
                          onToggleEye={() => setBrowserGroupVisible('rooms', !groupVisible('rooms'))}
                          dimmed={!groupVisible('rooms')}
                          icon={<Square size={12} />}
                        />
                        {expanded.rooms &&
                          floorPlan!.rooms.map((r) => {
                            const selected = selectedFloorPlanRoomId === r.id;
                            const visible = isVisibleOnCanvas(design, 'room', r);
                            return (
                              <Row theme={theme}
                                key={r.id}
                                depth={3}
                                label={r.name || 'Room'}
                                selected={selected}
                                dimmed={!visible}
                                eyeVisible={visible}
                                onToggleEye={() => setItemHidden('room', r.id, !r.hidden)}
                                icon={<Square size={11} />}
                                onClick={() => {
                                  clearOtherSelections();
                                  setSelectedEquipment(null);
                                  setSelectedCable(null);
                                  setSelectedRack(null);
                                  setCanvasMode('select');
                                  setSelectedFloorPlanRoomId(r.id);
                                }}
                                onDoubleClick={() => onRenameRoom?.(r.id)}
                              />
                            );
                          })}
                      </>
                    )}

                    {doors.length > 0 && (
                      <>
                        <Row theme={theme}
                          depth={2}
                          label={`Doors (${doors.length})`}
                          hasChildren
                          expanded={!!expanded.doors}
                          onToggleExpand={() => toggleExpanded('doors')}
                          eyeVisible={groupVisible('doors')}
                          onToggleEye={() => setBrowserGroupVisible('doors', !groupVisible('doors'))}
                          dimmed={!groupVisible('doors')}
                          icon={<DoorOpen size={12} />}
                        />
                        {expanded.doors &&
                          doors.map((d, i) => {
                            const selected = selectedFloorPlanOpeningId === d.id;
                            const visible = isVisibleOnCanvas(design, 'door', d);
                            return (
                              <Row theme={theme}
                                key={d.id}
                                depth={3}
                                label={`Door ${i + 1}`}
                                selected={selected}
                                dimmed={!visible}
                                eyeVisible={visible}
                                onToggleEye={() => setItemHidden('door', d.id, !d.hidden)}
                                icon={<DoorOpen size={11} />}
                                onClick={() => {
                                  clearOtherSelections();
                                  setSelectedEquipment(null);
                                  setSelectedCable(null);
                                  setSelectedRack(null);
                                  setCanvasMode('select');
                                  setSelectedFloorPlanOpeningId(d.id);
                                }}
                              />
                            );
                          })}
                      </>
                    )}

                    {refs.length > 0 && (
                      <>
                        <Row theme={theme}
                          depth={2}
                          label={`Reference Images (${refs.length})`}
                          hasChildren
                          expanded={!!expanded.refs}
                          onToggleExpand={() => toggleExpanded('refs')}
                          eyeVisible={groupVisible('referenceImages')}
                          onToggleEye={() =>
                            setBrowserGroupVisible('referenceImages', !groupVisible('referenceImages'))
                          }
                          dimmed={!groupVisible('referenceImages')}
                          icon={<ImageIcon size={12} />}
                        />
                        {expanded.refs &&
                          refs.map((img, i) => {
                            const selected = selectedReferenceImageId === img.id;
                            const visible = isVisibleOnCanvas(design, 'referenceImage', img);
                            return (
                              <React.Fragment key={img.id}>
                                <Row theme={theme}
                                  depth={3}
                                  label={img.name?.trim() || `Reference ${i + 1}`}
                                  selected={selected}
                                  dimmed={!visible}
                                  eyeVisible={visible}
                                  onToggleEye={() => setItemHidden('referenceImage', img.id, !img.hidden)}
                                  icon={<ImageIcon size={11} />}
                                  onClick={() => {
                                    clearOtherSelections();
                                    setSelectedEquipment(null);
                                    setSelectedCable(null);
                                    setSelectedRack(null);
                                    setCanvasMode('select');
                                    setSelectedReferenceImageId(img.id);
                                  }}
                                  onDoubleClick={() => {
                                    setCanvasMode('select');
                                    setSelectedReferenceImageId(img.id);
                                  }}
                                />
                                {selected && (
                                  <div
                                    className="flex items-center gap-1.5 min-w-0 overflow-hidden box-border"
                                    style={{
                                      width: '100%',
                                      maxWidth: '100%',
                                      paddingLeft: 4 + 3 * 12 + 28,
                                      paddingRight: 8,
                                      height: 22,
                                      backgroundColor: theme.rowBg,
                                    }}
                                  >
                                    <span className="text-[10px] flex-shrink-0" style={{ color: muted }}>
                                      Opacity
                                    </span>
                                    <input
                                      type="range"
                                      min={10}
                                      max={100}
                                      value={Math.round((img.opacity ?? 0.45) * 100)}
                                      className="min-w-0 flex-1 accent-violet-400 h-3"
                                      style={{ width: 0 }}
                                      onPointerDown={() =>
                                        useProjectStore.getState().snapshotFloorPlanForDrag()
                                      }
                                      onChange={(e) => {
                                        const opacity = Math.min(
                                          1,
                                          Math.max(0.1, Number(e.target.value) / 100)
                                        );
                                        updateFloorPlanReferenceImage(img.id, { opacity }, false);
                                      }}
                                    />
                                  </div>
                                )}
                              </React.Fragment>
                            );
                          })}
                      </>
                    )}
                  </>
                )}
              </>
            ) : null}

            {/* Equipment */}
            {(design?.equipment?.length ?? 0) > 0 && (
              <>
                <Row theme={theme}
                  depth={1}
                  label={`Equipment (${design!.equipment.length})`}
                  hasChildren
                  expanded={!!expanded.equipment}
                  onToggleExpand={() => toggleExpanded('equipment')}
                  eyeVisible={groupVisible('equipment')}
                  onToggleEye={() => setBrowserGroupVisible('equipment', !groupVisible('equipment'))}
                  dimmed={!groupVisible('equipment')}
                  icon={<Package size={12} />}
                />
                {expanded.equipment &&
                  Array.from(equipmentByType.entries()).map(([typeKey, items]) => {
                    const typeVis = isEquipmentTypeVisible(design, typeKey);
                    const typeExpKey = `eq-${typeKey}`;
                    return (
                      <React.Fragment key={typeKey}>
                        <Row theme={theme}
                          depth={2}
                          label={`${TYPE_LABELS[typeKey] || typeKey} (${items.length})`}
                          hasChildren
                          expanded={!!expanded[typeExpKey]}
                          onToggleExpand={() => toggleExpanded(typeExpKey)}
                          eyeVisible={typeVis}
                          onToggleEye={() =>
                            setBrowserEquipmentTypeVisible(typeKey, !typeVis)
                          }
                          dimmed={!typeVis}
                          icon={equipmentTypeIcon(typeKey, 11)}
                        />
                        {expanded[typeExpKey] &&
                          items.map((eq) => {
                            const selected = selectedEquipment?.id === eq.id;
                            const visible = isVisibleOnCanvas(design, 'equipment', eq, eq.equipment.type);
                            const label = eq.config?.label
                              ? `${eq.config.label} · ${eq.equipment.name}`
                              : eq.equipment.name;
                            return (
                              <Row theme={theme}
                                key={eq.id}
                                depth={3}
                                label={label}
                                selected={selected}
                                dimmed={!visible}
                                eyeVisible={visible}
                                onToggleEye={() => setItemHidden('equipment', eq.id, !eq.hidden)}
                                icon={equipmentTypeIcon(eq.equipment.type, 11)}
                                onMouseEnter={() => deferCanvasHover(() => setHoveredEquipmentId(eq.id))}
                                onMouseLeave={() => deferCanvasHover(() => setHoveredEquipmentId(null))}
                                onClick={() => {
                                  clearOtherSelections();
                                  setSelectedCable(null);
                                  setSelectedRack(null);
                                  setSelectedEquipment(eq);
                                  useLogicStore.getState().setFocusDeviceId(eq.id);
                                }}
                                onDoubleClick={() => {
                                  clearOtherSelections();
                                  setSelectedCable(null);
                                  setSelectedRack(null);
                                  setSelectedEquipment(eq);
                                  setSelectedEquipmentForPortView(eq);
                                  setEquipmentConfigOpen(false);
                                  setPortVisualizationOpen(true);
                                  requestRestoreFloatingEquipmentPanel();
                                  useLogicStore.getState().setFocusDeviceId(eq.id);
                                }}
                              />
                            );
                          })}
                      </React.Fragment>
                    );
                  })}
              </>
            )}

            {/* Cables */}
            {cables.length > 0 && (
              <>
                <Row theme={theme}
                  depth={1}
                  label={`Cable Runs (${cables.length})`}
                  hasChildren
                  expanded={!!expanded.cables}
                  onToggleExpand={() => toggleExpanded('cables')}
                  eyeVisible={groupVisible('cables')}
                  onToggleEye={() => setBrowserGroupVisible('cables', !groupVisible('cables'))}
                  dimmed={!groupVisible('cables')}
                  icon={<Cable size={12} />}
                />
                {expanded.cables &&
                  cables.map((cable) => {
                    const selected = selectedCable?.id === cable.id;
                    const visible = isVisibleOnCanvas(design, 'cable', cable);
                    const label =
                      cable.label ||
                      (cable.subtype ? cable.subtype : `${cable.type} Cable`);
                    return (
                      <Row theme={theme}
                        key={cable.id}
                        depth={2}
                        label={label}
                        selected={selected}
                        dimmed={!visible}
                        eyeVisible={visible}
                        onToggleEye={() => setItemHidden('cable', cable.id, !cable.hidden)}
                        icon={<Cable size={11} />}
                        onMouseEnter={() => deferCanvasHover(() => setHoveredCableId(cable.id))}
                        onMouseLeave={() => deferCanvasHover(() => setHoveredCableId(null))}
                        onClick={() => {
                          clearOtherSelections();
                          setSelectedEquipment(null);
                          setSelectedRack(null);
                          setSelectedCable(cable);
                        }}
                        onDoubleClick={() => {
                          clearOtherSelections();
                          setSelectedEquipment(null);
                          setSelectedRack(null);
                          setSelectedCable(cable);
                          setCablePortEditorOpen(true);
                        }}
                      />
                    );
                  })}
              </>
            )}

            {/* Racks */}
            {(design?.racks?.length ?? 0) > 0 && (
              <>
                <Row theme={theme}
                  depth={1}
                  label={`Racks (${design!.racks.length})`}
                  hasChildren
                  expanded={!!expanded.racks}
                  onToggleExpand={() => toggleExpanded('racks')}
                  eyeVisible={groupVisible('racks')}
                  onToggleEye={() => setBrowserGroupVisible('racks', !groupVisible('racks'))}
                  dimmed={!groupVisible('racks')}
                  icon={<Server size={12} />}
                />
                {expanded.racks &&
                  design!.racks.map((rack) => {
                    const selected = selectedRack?.id === rack.id;
                    const visible = isVisibleOnCanvas(design, 'rack', rack);
                    return (
                      <Row theme={theme}
                        key={rack.id}
                        depth={2}
                        label={rack.name || `${rack.size}U Rack`}
                        selected={selected}
                        dimmed={!visible}
                        eyeVisible={visible}
                        onToggleEye={() => setItemHidden('rack', rack.id, !rack.hidden)}
                        icon={<Server size={11} />}
                        onClick={() => {
                          clearOtherSelections();
                          setSelectedEquipment(null);
                          setSelectedCable(null);
                          setSelectedRack(rack);
                        }}
                        onDoubleClick={() => {
                          clearOtherSelections();
                          setSelectedEquipment(null);
                          setSelectedCable(null);
                          setSelectedRack(rack);
                          setRackBuilderOpen(true);
                        }}
                      />
                    );
                  })}
              </>
            )}

            {/* Boxes */}
            {boxes.length > 0 && (
              <>
                <Row theme={theme}
                  depth={1}
                  label={`Boxes (${boxes.length})`}
                  hasChildren
                  expanded={!!expanded.boxes}
                  onToggleExpand={() => toggleExpanded('boxes')}
                  eyeVisible={groupVisible('boxes')}
                  onToggleEye={() => setBrowserGroupVisible('boxes', !groupVisible('boxes'))}
                  dimmed={!groupVisible('boxes')}
                  icon={<Square size={12} />}
                />
                {expanded.boxes &&
                  boxes.map((box, i) => {
                    const selected = selectedBoxId === box.id;
                    const groupOk = groupVisible('boxes');
                    const visible = groupOk && !box.hidden;
                    return (
                      <Row theme={theme}
                        key={box.id}
                        depth={2}
                        label={`Box ${i + 1}`}
                        selected={selected}
                        dimmed={!visible}
                        eyeVisible={visible}
                        onToggleEye={() => onToggleBoxHidden(box.id, !box.hidden)}
                        icon={
                          <span
                            className="inline-block w-2.5 h-2.5 rounded-sm border"
                            style={{ backgroundColor: box.stroke, borderColor: box.stroke }}
                          />
                        }
                        onClick={() => {
                          clearOtherSelections();
                          setSelectedEquipment(null);
                          setSelectedCable(null);
                          setSelectedRack(null);
                          onSelectBox(box.id);
                        }}
                      />
                    );
                  })}
              </>
            )}

            {/* Labels */}
            {labels.length > 0 && (
              <>
                <Row theme={theme}
                  depth={1}
                  label={`Labels (${labels.length})`}
                  hasChildren
                  expanded={!!expanded.labels}
                  onToggleExpand={() => toggleExpanded('labels')}
                  eyeVisible={groupVisible('labels')}
                  onToggleEye={() => setBrowserGroupVisible('labels', !groupVisible('labels'))}
                  dimmed={!groupVisible('labels')}
                  icon={<Type size={12} />}
                />
                {expanded.labels &&
                  labels.map((lab) => {
                    const selected = selectedLabelId === lab.id;
                    const groupOk = groupVisible('labels');
                    const visible = groupOk && !lab.hidden;
                    return (
                      <Row theme={theme}
                        key={lab.id}
                        depth={2}
                        label={lab.text?.trim() || 'Label'}
                        selected={selected}
                        dimmed={!visible}
                        eyeVisible={visible}
                        onToggleEye={() => onToggleLabelHidden(lab.id, !lab.hidden)}
                        icon={<Type size={11} />}
                        onClick={() => {
                          clearOtherSelections();
                          setSelectedEquipment(null);
                          setSelectedCable(null);
                          setSelectedRack(null);
                          onSelectLabel(lab.id);
                        }}
                        onDoubleClick={() => onEditLabel(lab.id)}
                      />
                    );
                  })}
              </>
            )}
          </>
        )}
        </div>
      </div>
    </div>
  );
};

export default DesignBrowser;
